import React, { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { SUBJECT_TAXONOMY } from "../utils/SUBJECT_TAXONOMY";
import images from '../constants/images';
import topBg from "../images/TopBG.png";
import models from "../images/Models.png";
import preSchoolIcon from "../assets/Icons/Pre School.png";
import edTechIcon from "../assets/Icons/EdTech.png";
import universityIcon from "../assets/Icons/University.png";

const JobSeekerHomepage = () => {
  const navigate = useNavigate();
  const mobileSearchRef = React.useRef(null);

  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [experience, setExperience] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [allJobs, setAllJobs] = useState([]);

  const [institutions, setInstitutions] = useState([]);

  // Dropdown states
  const [showTitleDropdown, setShowTitleDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showExperienceDropdown, setShowExperienceDropdown] = useState(false);
  const normalizeText = (text = "") =>
    text
      .toLowerCase()
      .replace(/teacher jobs?|jobs?/g, "")
      .replace(/[-_/]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const matchesDesignation = (job, designation) => {
    if (!designation) return true;

    const key = normalizeText(designation);

    /* ===== NON-TEACHING ROLES ===== */
    if (job.category === "Non Teaching") {
      return normalizeText(job.title).includes(key);
    }

    /* ===== TEACHING ROLES ===== */
    const allowedSubjects =
      SUBJECT_TAXONOMY[key] || [key];

    const jobText = normalizeText(
      `${job.title} ${job.subject} ${job.description}`
    );

    return allowedSubjects.some(subject =>
      jobText.includes(subject)
    );
  };

  const scrollRef = React.useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -400, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 400, behavior: "smooth" });
    }
  };

  // Job title options
  const jobTitles = [
    'Mathematics Teacher Jobs',
    'English Teacher Jobs',
    'Teacher Jobs',
    'Biology Teacher Jobs',
    'Science Teacher Jobs',
    'Hindi Teacher Jobs',
    'Social Science Teacher Jobs',
    'Physics Teacher Jobs',
    'Chemistry Teacher Jobs',
    'Assistant Teacher Jobs',
    'Computer Science Teacher Jobs',
    'History Teacher Jobs',
    'Computer Teacher Jobs',
    'Commerce Teacher Jobs',
    'Economics Teacher Jobs',
    'Accountancy Teacher Jobs',
    'Academic Coordinator Jobs',
    'English Language Teacher Jobs',
    'General Teacher Jobs',
    'Geography Teacher Jobs',
    'Academic Counsellor Jobs',
    'Accountant Jobs',
    'Administration Executive Jobs',
    'Physical Education Teacher Jobs',
    'Political Science Teacher Jobs'
  ];

  // Location options (without "Teacher jobs in" prefix)
  const locations = [
    'Bangalore',
    'Hyderabad',
    'Lucknow',
    'Coimbatore',
    'Pune',
    'Ahmedabad',
    'Delhi',
    'Patna',
    'Chennai',
    'Mumbai',
    'Indore',
    'Agra',
    'Kolkata',
    'Jaipur',
    'Bhubaneswar'
  ];

  // Experience options
  const experienceOptions = [
    '0-2 years',
    '2-5 years',
    '5+ years',
  ];

  const institutionLogos = [
    images.Edutel,
    images.Global,
    images.JGI,
    images.MountLitera,
    images.Sankalp,
    images.Sanmarga,
    images.Shrikaraa,
    images.SLV,
    images.Takshila,
    images.Varada,
    images.VidyaSoudha,
  ];

  // Filter functions
  const filteredTitles = jobTitles.filter(job =>
    job.toLowerCase().includes(title.toLowerCase())
  );

  const filteredLocations = locations.filter(loc =>
    loc.toLowerCase().includes(location.toLowerCase())
  );

  const filteredExperience = experienceOptions.filter(exp =>
    exp.toLowerCase().includes(experience.toLowerCase())
  );
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileSearchRef.current &&
        !mobileSearchRef.current.contains(event.target)
      ) {
        setShowTitleDropdown(false);
        setShowLocationDropdown(false);
        setShowExperienceDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);



  const applySearchFilters = (jobs) => {
    const [minExp, maxExp] = getExperienceRange(experience);
    const designation = normalizeText(title);

    return jobs.filter(job => {
      const expMatch =
        job.min_experience <= maxExp &&
        job.max_experience >= minExp;

      const designationMatch = matchesDesignation(job, designation);

      const locationMatch = location
        ? job.address?.city?.toLowerCase().includes(location.toLowerCase())
        : true;

      return expMatch && designationMatch && locationMatch;
    });
  };

  const getExperienceRange = (label) => {
    if (label === '0-2 years') return [0, 2];
    if (label === '2-5 years') return [2, 5];
    if (label === '5+ years') return [5, 50];
    return [0, 50];
  };



  const fetchAllJobs = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/jobs/search-jobs`
      );
      setAllJobs(response.data);
    } catch (error) {
      console.error("Failed to load jobs:", error);
    }
  };
  const fetchAllInstitutions = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/profile/allInstitutions`
      );

      // backend returns { data: [...] }
      setInstitutions(response.data.data || []);
    } catch (error) {
      console.error("Failed to load institutions:", error);
    }
  };

  useEffect(() => {
    fetchAllInstitutions();
    fetchAllJobs(); // keep this for job listing page
  }, []);


  const handleSearch = async () => {
    if (!isUserLoggedIn()) {
      navigate("/login", { state: { redirectTo: "/" } });
      return;
    }

    const [minExp, maxExp] = getExperienceRange(experience);
    const designation = normalizeText(title);
    const today = new Date();

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/jobs/search-jobs`,
        {
          params: {
            location: location === "Bangalore" ? "Bengaluru" : location,
            minExp,
            maxExp,
          },
        }
      );

      const filtered = response.data.filter(job => {
        const expMatch =
          job.min_experience <= maxExp &&
          job.max_experience >= minExp;

        const designationMatch = matchesDesignation(job, designation);
        const isOpen = job.status === "open";
        const notExpired =
          !job.closing_date || new Date(job.closing_date) >= today;

        return expMatch && designationMatch && isOpen && notExpired;
      });

      setSearchResults(filtered);
      setSelectedInstitution(null);

    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  const handleTitleSelect = (selectedTitle) => {
    setTitle(selectedTitle);
    setShowTitleDropdown(false);
  };

  const handleLocationSelect = (selectedLocation) => {
    setLocation(selectedLocation);
    setShowLocationDropdown(false);
  };

  const handleExperienceSelect = (selectedExperience) => {
    setExperience(selectedExperience);
    setShowExperienceDropdown(false);
  };
  const isUserLoggedIn = () => {
    const token = localStorage.getItem("token");
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  };

  const getInstitutionLogo = (profilePic) => {
    if (!profilePic) return null;

    // full URL already
    if (profilePic.startsWith("http")) {
      return profilePic;
    }

    // If path already contains uploads/ (stored by backend)
    if (profilePic.includes("uploads")) {
      return `${process.env.REACT_APP_API_URL}/${profilePic}`;
    }

    // force correct backend static folder (legacy or just filename)
    return `${process.env.REACT_APP_API_URL}/uploads/profile_pics/${profilePic}`;
  };









  const trendingCategories = [
    {
      label: 'Pre-School',
      path: '/jobs?domain=pre-school',
      icon: preSchoolIcon,
      jobs: '348 Jobs',
    },
    {
      label: 'EdTech',
      path: '/jobs?domain=edtech',
      icon: edTechIcon,
      jobs: '668 Jobs',
    },
    {
      label: 'College',
      path: '/jobs?domain=college',
      icon: universityIcon,
      jobs: '165 Jobs',
    },
  ];

  const getEmploymentTypesForInstitution = (institutionId) => {
    const today = new Date();

    const jobs = allJobs.filter(job => {
      const instId = job.Institution_id?._id || job.Institution_id;

      return (
        instId?.toString() === institutionId?.toString() &&
        job.status === "open" &&
        (!job.closing_date || new Date(job.closing_date) >= today)
      );
    });

    const types = new Set();
    jobs.forEach(job => {
      if (job.employment_type) {
        types.add(job.employment_type);
      }
    });

    return Array.from(types);
  };



  const getLocationForInstitution = (institutionId) => {
    const today = new Date();

    const job = allJobs.find(job => {
      const instId = job.Institution_id?._id || job.Institution_id;

      return (
        instId?.toString() === institutionId?.toString() &&
        job.status === "open" &&
        (!job.closing_date || new Date(job.closing_date) >= today) &&
        job.address?.city
      );
    });

    if (!job) return null;

    const { area, city, state } = job.address;

    return [area, city, state].filter(Boolean).join(", ");
  };
  const getOpenJobsCountForInstitution = (institutionId) => {
    const today = new Date();

    return allJobs.filter(job => {
      const instId = job.Institution_id?._id || job.Institution_id;

      const isSameInstitution =
        instId?.toString() === institutionId?.toString();

      const isOpen = job.status === "open";

      const notExpired =
        !job.closing_date || new Date(job.closing_date) >= today;

      return isSameInstitution && isOpen && notExpired;
    }).length;
  };

  return (
    <div className="min-h-screen font-sans bg-gray-50">
      {/* ================= DESKTOP VIEW ================= */}
      <div className="hidden md:block">

        {/* HERO WITH GRADIENT / IMAGE BACKGROUND */}
        <div
          className="relative overflow-hidden"
          style={{
            backgroundImage: `url(${topBg})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Navbar inside same background */}
          <Navbar />

          {/* Hero content */}

          <div className="hero-tight relative flex items-center px-6 pt-10 pb-10 h-[calc(100vh-80px)] max-h-[720px]">




            {/* Left text + search */}
            <div className="w-full md:w-1/2 z-10 pl-5">
              <h1 className="text-[28px] md:text-[36px] lg:text-[44px] font-extrabold text-gray-800 mb-3">
                Find Your Dream Teaching Job
              </h1>

              <p className="w-full max-w-[516px] text-[16px] md:text-[18px] leading-[24px] font-normal text-black mb-[24px]">
                Discover 5+ Lakh teaching jobs across India & beyond
              </p>




              {/* SEARCH + DROPDOWNS WRAPPER */}
              <div className="relative w-full max-w-3xl">

                {/* SEARCH BAR */}
                <div className="relative flex items-center h-[56px] bg-white rounded-full border border-gray-200 shadow overflow-hidden pr-[110px]">


                  {/* Title */}
                  <div className="flex items-center flex-1 pl-5">
                    <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Type job title"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        setShowTitleDropdown(true);
                      }}
                      onFocus={() => setShowTitleDropdown(true)}
                      className="w-full bg-transparent outline-none text-sm placeholder-gray-400"
                    />
                  </div>

                  <div className="w-px h-8 bg-gray-300 mx-2" />

                  {/* Location */}
                  <div className="flex items-center flex-1">
                    <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                    <input
                      type="text"
                      placeholder="Location"
                      value={location}
                      onChange={(e) => {
                        setLocation(e.target.value);
                        setShowLocationDropdown(true);
                      }}
                      onFocus={() => setShowLocationDropdown(true)}
                      className="w-full bg-transparent outline-none text-sm placeholder-gray-400"
                    />
                  </div>

                  <div className="w-px h-8 bg-gray-300 mx-2" />

                  {/* Experience */}
                  <div className="flex items-center w-40">
                    <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <polygon points="12 2 15 9 22 9 17 14 19 22 12 18 5 22 7 14 2 9 9 9" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Experience"
                      value={experience}
                      onChange={(e) => {
                        setExperience(e.target.value);
                        setShowExperienceDropdown(true);
                      }}
                      onFocus={() => setShowExperienceDropdown(true)}
                      className="w-full bg-transparent outline-none text-sm placeholder-gray-400"
                    />
                  </div>

                  {/* SEARCH BUTTON */}
                  <button
                    onClick={() => {
                      handleSearch();
                      setShowTitleDropdown(false);
                      setShowLocationDropdown(false);
                      setShowExperienceDropdown(false);
                    }}
                    className="
    absolute
    right-[3px]
    top-[3px]
    bottom-[3px]
    px-6
    rounded-full
    text-white
    text-sm
    font-semibold
    bg-gradient-to-r from-blue-500 to-cyan-500
    hover:opacity-90
    flex items-center justify-center
  "
                  >
                    Search
                  </button>



                </div>

                {/* TITLE DROPDOWN */}
                {showTitleDropdown && filteredTitles.length > 0 && (
                  <div className="absolute left-0 top-[64px] w-1/3 bg-white border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    {filteredTitles.map((jobTitle, i) => (
                      <div
                        key={i}
                        onClick={() => handleTitleSelect(jobTitle)}
                        className="px-4 py-2 cursor-pointer hover:bg-blue-50"
                      >
                        {jobTitle}
                      </div>
                    ))}
                  </div>
                )}

                {/* LOCATION DROPDOWN */}
                {showLocationDropdown && filteredLocations.length > 0 && (
                  <div className="absolute left-1/3 top-[64px] w-1/3 bg-white border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    {filteredLocations.map((loc, i) => (
                      <div
                        key={i}
                        onClick={() => handleLocationSelect(loc)}
                        className="px-4 py-2 cursor-pointer hover:bg-blue-50"
                      >
                        {loc}
                      </div>
                    ))}
                  </div>
                )}

                {/* EXPERIENCE DROPDOWN */}
                {showExperienceDropdown && filteredExperience.length > 0 && (
                  <div className="absolute right-0 top-[64px] w-40 bg-white border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    {filteredExperience.map((exp, i) => (
                      <div
                        key={i}
                        onClick={() => handleExperienceSelect(exp)}
                        className="px-4 py-2 cursor-pointer hover:bg-blue-50"
                      >
                        {exp}
                      </div>
                    ))}
                  </div>
                )}

              </div>



              {/* Stats */}
              <div className="flex gap-[155px] mt-10 text-gray-800">

                <div>
                  <p className="text-[32px] leading-[140%] tracking-[-0.025em] font-bold text-black">
                    550+
                  </p>

                  <p className="text-[16px] leading-[140%] tracking-[-0.025em] font-medium text-[#9A9A9A]">
                    Job list
                  </p>

                </div>
                <div>
                  <p className="text-[32px] leading-[140%] tracking-[-0.025em] font-bold text-black">3600+</p>
                  <p className="text-[16px] leading-[140%] tracking-[-0.025em] font-medium text-[#9A9A9A]">People Hired</p>
                </div>
                <div>
                  <p className="text-[32px] leading-[140%] tracking-[-0.025em] font-bold text-black">800+</p>
                  <p className="text-[16px] leading-[140%] tracking-[-0.025em] font-medium text-[#9A9A9A]">Schools</p>
                </div>
              </div>
            </div>

            {/* Right side image */}
            <div className="absolute right-0 bottom-0 h-full w-1/2 flex items-end justify-end pr-6">

              <img
                src={models}
                alt="Teachers"
                className="h-[85%] max-h-[520px] w-auto object-contain"
              />
            </div>
          </div>
        </div>

        {/* Click outside to close dropdowns */}
        {(showTitleDropdown || showLocationDropdown || showExperienceDropdown) && (
          <div
            className="absolute inset-0 z-0"
            onClick={() => {
              setShowTitleDropdown(false);
              setShowLocationDropdown(false);
              setShowExperienceDropdown(false);
            }}
          />
        )}


        {/* SEARCH RESULTS */}
        {isUserLoggedIn() && searchResults.length > 0 && (

          <section className="px-6 mb-16 pt-5">

            <h2 className="mb-6 text-2xl font-semibold text-center text-gray-800">
              Search Results
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 px-6">
              {searchResults.map((job, index) => (
                <div key={index} className="p-4 bg-white border rounded-lg shadow hover:shadow-lg transition hover:scale-[1.02]">
                  <h3 className="mb-1 text-xl font-bold text-gray-800">{job.title}</h3>
                  <p className="text-sm text-gray-600">{job.address?.city}, {job.address?.state}</p>
                  <span className="inline-block px-2 py-1 mt-2 text-xs font-medium text-white bg-blue-500 rounded-full">
                    {job.min_experience} - {job.max_experience} yrs
                  </span>
                  <p className="mt-2 text-sm text-gray-600 line-clamp-3">{job.description}</p>
                  <button
                    onClick={() => {
                      if (!isUserLoggedIn()) {
                        navigate("/login", { state: { redirectTo: `/job-description/${job._id}` } });
                      } else {
                        navigate(`/job-description/${job._id}`);
                      }
                    }}
                    className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    View More
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
        <section className="px-6 mt-[40px]">


          <h2 className="w-[244px] mx-auto text-[24px] leading-[24px] font-medium text-black text-center mb-[20px]">

            Institutions We Serve
          </h2>


          {/* padded content wrapper */}
          <div className="relative overflow-hidden  ">

            {/* LEFT FADE */}
            <div className="pointer-events-none absolute left-0 top-0 h-full w-24 z-10
                    bg-gradient-to-r from-gray-50 to-transparent px-10" />

            {/* RIGHT FADE */}
            <div className="pointer-events-none absolute right-0 top-0 h-full w-24 z-10
                    bg-gradient-to-l from-gray-50 to-transparent" />

            {/* MARQUEE */}
            <div className="flex gap-8 w-max animate-marquee">
              {[...institutionLogos, ...institutionLogos].map((logo, index) => (
                <div
                  key={index}
                  className="w-[150px] h-[110px] flex items-center justify-center"
                >
                  <img
                    src={logo}
                    alt={`institution-${index}`}
                    className="h-[80px] w-auto object-contain"
                  />
                </div>

              ))}
            </div>

          </div>
        </section>




        <section className="px-6 mb-16 pt-7">
          <h2 className="text-center text-[24px] leading-[24px] font-medium text-black mb-[58px]">
            Trending on This Website Today
          </h2>


          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 justify-items-center">

            {trendingCategories.map((category, index) => (
              <div
                key={index}
                onClick={() => navigate(category.path)}
                className="
      relative
      w-[288px]
      h-[136px]
      bg-[#D8ECFE]
      rounded-[8px]
      shadow-[0px_4px_12px_rgba(0,0,0,0.08)]
      cursor-pointer
      flex
      flex-col
      items-center
      pt-[44px]
    "
              >

                {/* Icon Circle */}
                <div
                  className="
        absolute
        -top-[40px]
        w-[80px]
        h-[80px]
        rounded-full
        bg-[#F7B9DE]
        flex
        items-center
        justify-center
      "
                >
                  <img
                    src={category.icon}
                    alt={category.label}
                    className="w-[40px] h-[40px] object-contain"
                  />
                </div>

                {/* Label */}
                <p className="text-[20px] leading-[24px] font-bold text-black mb-[4px]">
                  {category.label}
                </p>

                {/* Jobs */}
                <p className="text-[16px] leading-[24px] text-[#6B7280]">
                  {category.jobs}
                </p>

              </div>
            ))}

          </div>
        </section>


        {!selectedInstitution && institutions.length > 0 && (
          <section className="px-6 mb-16">

            {/* HEADING */}
            <h2 className="text-center text-[24px] leading-[24px] font-medium text-black mb-[38px]">
              Registered Institutions
            </h2>

            {/* FULL-WIDTH WRAPPER (MATCHES HERO) */}
            <div className="relative">

              {/* LEFT ARROW — SAME LEFT EDGE AS HERO */}
              <button
                onClick={scrollLeft}
                className="
    absolute
    left-0
    top-1/2
    -translate-y-1/2
    w-[40px]
    h-[40px]
    flex items-center
    justify-center
    z-10
  "
              >
                <svg
                  width="12.33"
                  height="20"
                  viewBox="0 0 12 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11 1L1 10L11 19"
                    stroke="#79747E"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>


              {/* RIGHT ARROW — SAME RIGHT EDGE AS HERO */}
              <button
                onClick={scrollRight}
                className="
    absolute
    right-0
    top-1/2
    -translate-y-1/2
    w-[40px]
    h-[40px]
    flex items-center
    justify-center
    z-10
  "
              >
                <svg
                  width="12.33"
                  height="20"
                  viewBox="0 0 12 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L11 10L1 19"
                    stroke="#79747E"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>


              {/* GRID — FULL WIDTH */}
              <div className="grid grid-cols-3 gap-8 px-14">

                {institutions.map(inst => (
                  <div
                    key={inst._id}
                    onClick={() => {
                      if (!isUserLoggedIn()) {
                        navigate("/login", { state: { redirectTo: "/" } });
                        return;
                      }
                      setSelectedInstitution(inst._id);
                    }}

                    className="
             bg-[#F3F3F3]

              rounded-xl
              p-6
              shadow-sm
              hover:shadow-md
              transition
              cursor-pointer
            "
                  >
                    {/* TOP ROW */}
                    <div className="flex items-center gap-4">
                      {inst.profile_pic ? (
                        <img
                          src={getInstitutionLogo(inst.profile_pic)}
                          alt={inst.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-red-400 flex items-center justify-center text-white font-bold">
                          {inst.name?.charAt(0)}
                        </div>
                      )}

                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {inst.name}
                        </h3>

                        <p className="text-sm text-gray-500">
                          {getOpenJobsCountForInstitution(inst._id)} Openings
                        </p>

                        {getLocationForInstitution(inst._id) && (
                          <p className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                            <MapPin size={12} />
                            {getLocationForInstitution(inst._id)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* BADGES */}
                    <div className="flex gap-3 mt-4 flex-wrap">
                      {getEmploymentTypesForInstitution(inst._id).map(type => (
                        <span
                          key={type}
                          className={`px-4 py-1 text-xs rounded-full ${type === "Full time"
                            ? "bg-green-100 text-green-700"
                            : type === "Part time"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-blue-100 text-blue-700"
                            }`}
                        >
                          {type}
                        </span>
                      ))}
                    </div>

                  </div>
                ))}

              </div>
            </div>
          </section>
        )}


        {isUserLoggedIn() && selectedInstitution && (

          <section className="mb-16 px-6">
            <button
              onClick={() => setSelectedInstitution(null)}
              className="text-blue-600 mb-4"
            >
              ← Back to Institutions
            </button>

            <div className="grid  px-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {applySearchFilters(
                allJobs.filter(job => {
                  const instId = job.Institution_id?._id || job.Institution_id;

                  const isSameInstitution =
                    instId?.toString() === selectedInstitution?.toString();

                  const isOpen = job.status === "open";

                  const notExpired =
                    !job.closing_date || new Date(job.closing_date) >= new Date();

                  return isSameInstitution && isOpen && notExpired;
                })

              ).map(job => (
                <div
                  key={job._id}
                  className="p-4 bg-white border rounded-lg shadow hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                >
                  <h3 className="mb-1 text-xl font-bold text-gray-800">{job.title}</h3>
                  <p className="text-sm text-gray-600">
                    {job.address?.city}, {job.address?.state}
                  </p>
                  <span className="inline-block px-2 py-1 mt-2 text-xs font-medium text-white bg-blue-500 rounded-full">
                    {job.min_experience} - {job.max_experience} yrs
                  </span>
                  <p className="mt-2 text-sm text-gray-600 line-clamp-3">
                    {job.description}
                  </p>
                  <button
                    onClick={() => {
                      if (!isUserLoggedIn()) {
                        navigate("/login", {
                          state: { redirectTo: `/job-description/${job._id}` }
                        });
                      } else {
                        navigate(`/job-description/${job._id}`);
                      }
                    }}
                    className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition"
                  >
                    View More
                  </button>
                </div>
              ))}
            </div>

          </section>
        )}






        <Footer />
        {/* Floating WhatsApp Icon */}

        {!isUserLoggedIn() && (
          <a
            href="https://wa.me/+919739866955"
            target="_blank"
            rel="noopener noreferrer"
            className="fixed z-50 p-3 transition-colors duration-300 bg-green-500 rounded-full shadow-lg bottom-5 right-5 hover:bg-green-600"
          >
            <img
              src={images.WhatsaApp}
              alt="WhatsApp"
              className="w-10 h-10"
            />
          </a>
        )}
      </div>



      {/* ================= MOBILE VIEW ================= */}
      <div className="block md:hidden min-h-screen bg-[#F3F4F6]">

        <div className="bg-white pb-[32px]">

          {/* ================= MOBILE HERO (FIGMA EXACT) ================= */}
          <div
            className="block md:hidden relative w-full overflow-hidden"
            style={{
              backgroundImage: `url(${topBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="px-4 pt-5 pb-0">

              <Navbar />

              {/* Heading */}
              <div className="text-center mt-6">
                <h1 className="text-[24px] sm:text-[30px] md:text-[36px] leading-[32px] sm:leading-[40px] md:leading-[48px] font-extrabold text-[#464646] text-center">
                  Find Your Dream Teaching Job
                </h1>


                <p className="mt-[12px] text-[18px] leading-[24px] font-medium text-black">
                  Discover 5+ Lakh teaching jobs across India & beyond
                </p>

              </div>

              <div
                ref={mobileSearchRef}
                className="flex justify-center mt-6 relative"
              >

                <div className="relative flex items-center h-[48px] bg-white rounded-full border border-gray-200 shadow overflow-hidden pr-[80px] w-full max-w-[630px]">

                  {/* Title */}
                  <div className="flex items-center flex-1 pl-3">
                    <svg className="w-4 h-4 text-gray-400 mr-2 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        setShowTitleDropdown(true);
                        setShowLocationDropdown(false);
                        setShowExperienceDropdown(false);
                      }}
                      onFocus={() => {
                        setShowTitleDropdown(true);
                        setShowLocationDropdown(false);
                        setShowExperienceDropdown(false);
                      }}
                      placeholder="Job title"
                      className="w-full bg-transparent outline-none text-xs placeholder-gray-400"
                    />
                  </div>

                  <div className="w-px h-6 bg-gray-300" />

                  {/* Location */}
                  <div className="flex items-center flex-1 pl-2">
                    <MapPin className="w-4 h-4 text-gray-400 mr-2 shrink-0" />
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => {
                        setLocation(e.target.value);
                        setShowLocationDropdown(true);
                        setShowTitleDropdown(false);
                        setShowExperienceDropdown(false);
                      }}
                      onFocus={() => {
                        setShowLocationDropdown(true);
                        setShowTitleDropdown(false);
                        setShowExperienceDropdown(false);
                      }}
                      placeholder="Location"
                      className="w-full bg-transparent outline-none text-xs placeholder-gray-400"
                    />
                  </div>

                  <div className="w-px h-6 bg-gray-300" />

                  {/* Experience */}
                  <div className="flex items-center flex-1 pl-2">
                    <svg className="w-4 h-4 text-gray-400 mr-2 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <polygon points="12 2 15 9 22 9 17 14 19 22 12 18 5 22 7 14 2 9 9 9" />
                    </svg>
                    <input
                      type="text"
                      value={experience}
                      onChange={(e) => {
                        setExperience(e.target.value);
                        setShowExperienceDropdown(true);
                        setShowTitleDropdown(false);
                        setShowLocationDropdown(false);
                      }}
                      onFocus={() => {
                        setShowExperienceDropdown(true);
                        setShowTitleDropdown(false);
                        setShowLocationDropdown(false);
                      }}
                      placeholder="Exp"
                      className="w-full bg-transparent outline-none text-xs placeholder-gray-400"
                    />
                  </div>

                  {/* SEARCH BUTTON */}
                  <button
                    onClick={() => {
                      handleSearch();
                      setShowTitleDropdown(false);
                      setShowLocationDropdown(false);
                      setShowExperienceDropdown(false);
                    }}
                    className="
    absolute
    right-[3px]
    top-[3px]
    bottom-[3px]
    px-4
    rounded-full
    text-white
    text-xs
    font-semibold
    bg-gradient-to-r from-blue-500 to-cyan-500
    hover:opacity-90
    flex items-center justify-center
  "
                  >
                    Search
                  </button>

                  {showTitleDropdown && filteredTitles.length > 0 && (
                    <div className="absolute top-[52px] left-0 w-1/3 bg-white border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                      {filteredTitles.map((jobTitle, i) => (
                        <div
                          key={i}
                          onClick={() => handleTitleSelect(jobTitle)}
                          className="px-4 py-2 text-xs cursor-pointer hover:bg-blue-50"
                        >
                          {jobTitle}
                        </div>
                      ))}
                    </div>
                  )}



                  {showLocationDropdown && filteredLocations.length > 0 && (
                    <div className="absolute top-[52px] left-1/3 w-1/3 bg-white border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                      {filteredLocations.map((loc, i) => (
                        <div
                          key={i}
                          onClick={() => handleLocationSelect(loc)}
                          className="px-4 py-2 text-xs cursor-pointer hover:bg-blue-50"
                        >
                          {loc}
                        </div>
                      ))}
                    </div>
                  )}

                  {showExperienceDropdown && filteredExperience.length > 0 && (
                    <div className="absolute top-[52px] right-0 w-1/3 bg-white border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                      {filteredExperience.map((exp, i) => (
                        <div
                          key={i}
                          onClick={() => handleExperienceSelect(exp)}
                          className="px-4 py-2 text-xs cursor-pointer hover:bg-blue-50"
                        >
                          {exp}
                        </div>
                      ))}
                    </div>
                  )}




                </div>
              </div>







              {/* Stats */}
              <div className="mt-7 flex justify-center">
                <div className="w-[339px] h-[45px] flex items-center justify-around">

                  <div className="text-center">
                    <p className="text-[16px] font-semibold text-[#464646]">
                      550+
                    </p>
                    <p className="text-[10px] font-medium leading-[14px] tracking-[-0.25px] text-[#9A9A9A]">
                      Job list
                    </p>
                  </div>

                  <div className="text-center">
                    <p className="text-[16px] font-semibold text-[#464646]">
                      3600+
                    </p>
                    <p className="text-[10px] font-medium leading-[14px] tracking-[-0.25px] text-[#9A9A9A]">
                      People Hired
                    </p>
                  </div>

                  <div className="text-center">
                    <p className="text-[16px] font-semibold text-[#464646]">
                      800+
                    </p>
                    <p className="text-[10px] font-medium leading-[14px] tracking-[-0.25px] text-[#9A9A9A]">
                      Schools
                    </p>
                  </div>

                </div>
              </div>





              {/* Models Image */}
              <div className="mt-6 flex justify-center">
                <img
                  src={models}
                  alt="Teachers"
                  className="w-[80%] max-w-[300px] object-contain"
                />
              </div>
            </div>
          </div>



          {/* ===== INSTITUTIONS WE SERVE ===== */}

          <section className="px-4 mt-6 overflow-hidden">
            <h2 className="text-center text-[16px] leading-[16px] font-medium text-black mb-[24px]">
              Institutions We Serve
            </h2>


            <div className="relative overflow-hidden">

              {/* Left fade */}
              <div className="absolute left-0 top-0 h-full w-10 bg-gradient-to-r from-[#F4F7FB] to-transparent z-10" />

              {/* Right fade */}
              <div className="absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-[#F4F7FB] to-transparent z-10" />

              {/* Marquee Track */}
              <div className="mobile-marquee gap-4">

                {[...institutionLogos, ...institutionLogos].map((logo, index) => (
                  <div
                    key={index}
                    className="w-[80px] h-[55px] bg-white rounded-lg shadow-sm flex items-center justify-center"
                  >
                    <img
                      src={logo}
                      alt={`logo-${index}`}
                      className="max-h-[32px] object-contain"
                    />
                  </div>
                ))}

              </div>
            </div>
          </section>


          {/* ===== TRENDING ===== */}
          <section className="mt-[36px]">

            {/* TITLE */}
            <h2
              className="
    text-center
    text-[16px]
    leading-[16px]
    font-medium
    text-black
    mb-[24px]
  "
            >
              Trending On This Website Today
            </h2>


            {/* WRAPPER – responsive, was hardcoded 628px */}
            <div className="w-full max-w-[628px] mx-auto grid grid-cols-3 gap-3 px-4 pt-[16px]">

              {trendingCategories.map((category, index) => (
                <div
                  key={index}
                  onClick={() => navigate(category.path)}
                  className="relative cursor-pointer"
                >

                  {/* CARD */}
                  <div
                    className="
            w-full
            h-[82.64px]
            bg-[#D8ECFE]
            rounded-[8px]
            shadow-[0px_2px_5.8px_rgba(0,0,0,0.10)]
            flex
            flex-col
            items-center
            pt-[36px]
          "
                  >
                    <p
                      className="
              text-[14px]
              leading-[20px]
              font-bold
              text-[#1E1E1E]
            "
                    >
                      {category.label}
                    </p>

                    <p
                      className="
              text-[11px]
              leading-[20px]
              font-normal
              text-[#6B7280]
            "
                    >
                      {category.jobs}
                    </p>
                  </div>

                  {/* ICON CIRCLE */}
                  <div
                    className="
            absolute
            -top-[24px]
            left-1/2
            -translate-x-1/2
            w-[48px]
            h-[48px]
            rounded-full
            bg-[#F7B9DE]
            flex
            items-center
            justify-center
          "
                  >
                    <img
                      src={category.icon}
                      alt={category.label}
                      className={
                        category.label === "Pre-School"
                          ? "w-[32px] h-[32px] object-contain"
                          : "w-[40px] h-[40px] object-contain"
                      }
                    />

                  </div>

                </div>
              ))}

            </div>
          </section>



          {/* ===== REGISTERED INSTITUTIONS ===== */}
          <section className="mt-[40px]">

            {/* TITLE */}
            <h2 className="
  text-center
  text-[16px]
  leading-[24px]
  font-medium
  text-black
  mb-[24px]
">
              Registered Institutions
            </h2>


            {/* OUTER FRAME WIDTH – responsive, was hardcoded 700px */}
            <div className="w-full max-w-[700px] mx-auto relative px-4">

              {/* CENTER CONTENT BLOCK – responsive, was hardcoded 478px */}
              <div className="w-full max-w-[478px] mx-auto relative">

                {/* LEFT ARROW – positioned inside, was -left-[60px] offscreen */}

                <button
                  className="
    absolute
    -left-[36px]
    top-1/2
    -translate-y-1/2
    w-[32px]
    h-[32px]
    flex items-center
    justify-center
    z-10
  "
                >
                  <svg
                    width="12.33"
                    height="20"
                    viewBox="0 0 12 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11 1L1 10L11 19"
                      stroke="#79747E"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>


                {/* GRID – responsive, cards fill available space */}
                <div className="
        grid 
        grid-cols-2 
        gap-x-[12px] 
        gap-y-[12px]
      ">
                  {institutions?.slice(0, 6).map((inst) => (
                    <div
                      key={inst._id}
                      onClick={() => {
                        if (!isUserLoggedIn()) {
                          navigate("/login", { state: { redirectTo: "/" } });
                          return;
                        }
                        setSelectedInstitution(inst._id);
                      }}
                      className="
              w-full
              min-h-[85px] 
              bg-[#F3F3F3] 
              rounded-[12px] 
              px-[12px] 
              py-[10px]
              flex flex-col 
              justify-between
            "
                    >
                      {/* TOP SECTION */}
                      <div className="flex items-start gap-[8px]">

                        {/* Avatar */}
                        <div className="
                w-[32px] 
                h-[32px] 
                rounded-full 
                bg-[#F87171] 
                flex items-center 
                justify-center 
                text-white 
                text-[12px] 
                font-bold
                flex-shrink-0
              ">
                          {inst.name?.charAt(0)}
                        </div>

                        {/* Text Block */}
                        <div className="flex-1 min-w-0">

                          <p className="
                  text-[11px] 
                  font-medium 
                  text-black 
                  leading-[16px] 
                  truncate
                ">
                            {inst.name}
                          </p>

                          <p className="
                  text-[8px] 
                  font-normal 
                  text-[#9A9A9A] 
                  leading-[12px]
                ">
                            {getOpenJobsCountForInstitution(inst._id)} Openings
                          </p>

                          <p className="
                  text-[8px] 
                  font-normal 
                  text-[#9A9A9A] 
                  leading-[12px] 
                  truncate
                ">
                            {inst.location}
                          </p>

                        </div>
                      </div>

                      {/* TAGS */}
                      <div className="flex gap-[8px] flex-wrap mt-1">

                        <span className="
                text-[7px] 
                font-normal
                bg-[#FEE2E2] 
                text-[#EF4444] 
                px-[5px] 
                py-[1px] 
                rounded-full
              ">
                          Urgent
                        </span>

                        <span className="
                text-[7px] 
                font-normal
                bg-[#DCFCE7] 
                text-[#16A34A] 
                px-[5px] 
                py-[1px] 
                rounded-full
              ">
                          Full Time
                        </span>

                        <span className="
                text-[7px] 
                font-normal
                bg-[#FEF9C3] 
                text-[#CA8A04] 
                px-[5px] 
                py-[1px] 
                rounded-full
              ">
                          Part Time
                        </span>

                      </div>

                    </div>
                  ))}
                </div>

                {/* RIGHT ARROW – positioned inside, was -right-[60px] offscreen */}

                <button
                  className="
    absolute
    -right-[36px]
    top-1/2
    -translate-y-1/2
    w-[32px]
    h-[32px]
    flex items-center
    justify-center
    z-10
  "
                >
                  <svg
                    width="12.33"
                    height="20"
                    viewBox="0 0 12 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L11 10L1 19"
                      stroke="#79747E"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>


              </div>
            </div>
          </section>


        </div>




        <Footer />

        {/* MOBILE WHATSAPP FLOATING BUTTON */}
        {!isUserLoggedIn() && (
          <a
            href="https://wa.me/+919739866955"
            target="_blank"
            rel="noopener noreferrer"
            className="fixed bottom-5 right-5 z-50 bg-green-500 p-3 rounded-full shadow-lg hover:bg-green-600 transition"
          >
            <img
              src={images.WhatsaApp}
              alt="WhatsApp"
              className="w-8 h-8"
            />
          </a>
        )}

      </div>
    </div>

  );
};

export default JobSeekerHomepage;